import React from 'react';
import * as R from 'ramda';
import { branch, renderNothing, compose, withState, lifecycle } from 'recompose';
import styled from '@emotion/styled';
import { Box } from '@rebass/grid/emotion';
import '@influitive/infl-fonts/fonts.css';
import i18n from 'modules/localization';
import { I18nextProvider } from 'react-i18next';
import i18nMoment from '@influitive/moment-launcher';
import { hubConfig, windowObj, getToken } from 'modules/jwt-2';
import { withCookies } from 'react-cookie';

import {
  extractLocaleData,
  qaSel,
  isMobileApp,
  isWhiteLabel,
  isMaven,
  updatePathMap,
  checkSelectedHub,
  shouldLoadHubSwitcher
} from './common/utils';

import constants from './common/constants';
import MainHeader from './components/main-header';
import ThemeProvider from './theme-provider';
import ContextSwitcher from './components/context-switcher';
import {
  customBranding,
  profile,
  features,
  hubSwitcher,
  enabledFeatures,
  unseenChallengesCounts,
  publicHomepageTabs,
  notificationsCount,
  updateTimezone
} from './common/api';

const fontFamily = props => (
  props.theme.fonts.custom ? `${props.theme.fonts.custom.fontName}, ${constants.defaultFont}` : constants.defaultFont
);

const custom = props => `
  @font-face {
    font-family: ${props.theme.fonts.custom.fontName};
    font-style: normal;
    src: url(${props.theme.fonts.custom.fontUrl});
  }
`;

const App = styled(Box)`
  ${props => props.theme.fonts.custom && custom(props)}
  font-family: ${fontFamily};
`;

const switcher = profile => profile?.attributes.type === 'Corporate' && <ContextSwitcher />;

const updateProfileEvents = [
  'influitive:stage-completed',
  'influitive:waiting-confirmation',
  'influitive:reward-redeemed',
  'influitive:claimed-todays-points'
];

export default compose(
  withCookies,
  withState('customBranding', 'brandingReceived', {}),
  withState('i18n', 'i18nReceived', i18n({ defaultNs: 'AdvocateNavFE', ns: 'AdvocateNavFE' })),
  withState('moment', 'momentReceived', null),
  withState('profile', 'profileReceived', null),
  withState('features', 'linksReceived', []),
  withState('hubSwitcher', 'hubSwitcherReceived', null),
  withState('isAuth', 'isAuthReceived', false),
  withState('allowOpenSignUp', 'openSignUpReceived', false),
  withState('publicTabs', 'publicTabsReceived', []),
  withState('enabledFeatures', 'enabledFeaturesReceived', []),
  withState('unseenCounts', 'unseenCountsReceived', {}),
  withState('unseenNotificationsCount', 'unseenNotificationsCountReceived', 0),
  lifecycle({
    componentDidMount() {
      hubConfig().then(config => {
        this.props.openSignUpReceived(config.hub.allowOpenSignUp);

        publicHomepageTabs().then(this.props.publicTabsReceived);
        getToken().then(() => {
          windowObj().then(({ hub }) => {
            const { lang, primaryLang } = hub;

            const moment = i18nMoment({ lang });
            this.props.momentReceived(() => moment);

            const options = { defaultNs: 'AdvocateNavFE', ns: 'AdvocateNavFE', lang };
            this.props.i18nReceived(i18n(options));

            customBranding().then(branding => {
              this.props.brandingReceived({
                ...branding,
                header_html: extractLocaleData(branding.header_html, lang, primaryLang),
                header_css: extractLocaleData(branding.header_css, lang, primaryLang),
                banner_html: extractLocaleData(branding.banner_html, lang, primaryLang),
                banner_css: extractLocaleData(branding.banner_css, lang, primaryLang)
              });
            });
            notificationsCount().then(data => this.props.unseenNotificationsCountReceived(data.count));
          });
          this.props.isAuthReceived(true);
          const updateProfile = () => profile().then(this.props.profileReceived);
          updateProfile();

          // NOTE: some of these event are fired via jQuery's `trigger` and can't be handled with `addEventListener`
          // see https://github.com/jquery/jquery/issues/3347
          updateProfileEvents.forEach(event => $(window).on(event, updateProfile)); // eslint-disable-line no-undef

          features().then(this.props.linksReceived);
          shouldLoadHubSwitcher && hubSwitcher().then(hubs => {
            this.props.hubSwitcherReceived(hubs);
            unseenChallengesCounts(
              R.pluck('org_id',
                R.reject(checkSelectedHub, R.path(['family', 'companies'], hubs)))
            ).then(challengesCounts => {
              this.props.unseenCountsReceived(challengesCounts);
            });
          });
          // this is a temporary measure to always attempt to keep timezone in sync on-load
          updateTimezone();
        }).catch(err => {
          if (err.response.status === 401) {
            this.props.isAuthReceived(false);

            const lang = this.props.cookies.get('lang');

            const moment = i18nMoment({ lang });
            this.props.momentReceived(() => moment);

            const options = { defaultNs: 'AdvocateNavFE', ns: 'AdvocateNavFE', lang };
            this.props.i18nReceived(i18n(options));

            customBranding().then(branding => {
              this.props.brandingReceived({
                ...branding,
                header_html: extractLocaleData(branding.header_html),
                header_css: extractLocaleData(branding.header_css),
                banner_html: extractLocaleData(branding.banner_html),
                banner_css: extractLocaleData(branding.banner_css)
              });
            });
          }
        });
        enabledFeatures().then(features => {
          R.contains('discourse_homepage', features) && updatePathMap('forum', 'home');
          R.contains('mountain_dew', features) && updatePathMap('', 'challenges');
          this.props.enabledFeaturesReceived(features);
        });
      }).catch(() => {
        this.props.isAuthReceived(false);
      });
    },
    componenDidUnmount() {
      updateProfileEvents.forEach(event => $(window).off(event)); // eslint-disable-line no-undef
    }
  }),
  branch(({ customBranding }) => R.isEmpty(customBranding), renderNothing)
)(({ i18n, profile, ...rest }) => {
  return (
    <ThemeProvider customBranding={rest.customBranding}>
      <I18nextProvider i18n={i18n}>
        <App data-qa={qaSel('app')} id="ADVOCATE-NAV">
          {switcher(profile)}
          {(!isMobileApp() || isWhiteLabel || !isMaven) && <MainHeader profile={profile} {...rest} />}
        </App>
      </I18nextProvider>
    </ThemeProvider>
  );
});
